import {CommonModule} from '@angular/common';
import {Component, Injectable} from '@angular/core';
import {map, Observable, of, timer} from 'rxjs';

import {MaterialModule} from '../../../../material.module';
import {
  ConnectInternalAiTool,
  ConnectInternalAiToolsService
} from '../../../../services/connect/connect-internal-ai-tools.service';
import {IWorkflowFunctionBinding} from '../../../../models/function-options.model';
import {FunctionWorkflowBindingSelectorComponent} from './function-workflow-binding-selector.component';

const readyTool: ConnectInternalAiTool = {
  operationId: 'connect_lookup_order',
  toolName: 'connect_lookup_order',
  workflowId: 'workflow-orders-lookup',
  workflowName: 'Order Lookup',
  workflowVersion: 'v1',
  specHash: 'sha256:lookup',
  description: 'Looks up an order record by customer-facing reference.',
  triggerType: 'workflow',
  tags: ['orders', 'ready'],
  inputSchema: '{"type":"object","additionalProperties":false,"properties":{"orderNumber":{"type":"string"}},"required":["orderNumber"]}',
  readiness: {
    runtimeReady: true,
    allowlisted: true,
    signerConfigured: true,
    clientAllowed: true,
    replayReady: true,
    idempotencyReady: true,
    blockingReasons: []
  }
};

const blockedTool: ConnectInternalAiTool = {
  operationId: 'connect_track_order',
  toolName: 'connect_track_order',
  workflowId: 'workflow-orders-track',
  workflowName: 'Order Tracking',
  workflowVersion: 'v3',
  specHash: 'sha256:track',
  description: 'Returns shipment milestones when Connect runtime readiness is restored.',
  triggerType: 'workflow',
  tags: ['orders', 'runtime-blocked'],
  inputSchema: '{"type":"object","additionalProperties":false,"properties":{"trackingNumber":{"type":"string"}},"required":["trackingNumber"]}',
  readiness: {
    runtimeReady: false,
    allowlisted: false,
    signerConfigured: true,
    clientAllowed: true,
    replayReady: true,
    idempotencyReady: true,
    blockingReasons: ['operation_not_allowlisted']
  }
};

@Injectable()
class ReadyAndBlockedWorkflowBindingFixtureService {
  listTools(): Observable<ConnectInternalAiTool[]> {
    return of([readyTool, blockedTool]);
  }
}

@Injectable()
class LoadingWorkflowBindingFixtureService {
  listTools(): Observable<ConnectInternalAiTool[]> {
    return timer(3000).pipe(map(() => [readyTool]));
  }
}

@Component({
  selector: 'app-function-workflow-binding-loading-demo',
  standalone: true,
  imports: [FunctionWorkflowBindingSelectorComponent],
  providers: [{provide: ConnectInternalAiToolsService, useClass: LoadingWorkflowBindingFixtureService}],
  template: '<app-function-workflow-binding-selector></app-function-workflow-binding-selector>'
})
class FunctionWorkflowBindingLoadingDemoComponent {}

@Component({
  selector: 'app-function-workflow-binding-blocked-demo',
  standalone: true,
  imports: [FunctionWorkflowBindingSelectorComponent],
  providers: [{provide: ConnectInternalAiToolsService, useClass: ReadyAndBlockedWorkflowBindingFixtureService}],
  template: '<app-function-workflow-binding-selector></app-function-workflow-binding-selector>'
})
class FunctionWorkflowBindingBlockedDemoComponent {}

@Component({
  selector: 'app-function-workflow-binding-selection-demo',
  standalone: true,
  imports: [CommonModule, FunctionWorkflowBindingSelectorComponent],
  providers: [{provide: ConnectInternalAiToolsService, useClass: ReadyAndBlockedWorkflowBindingFixtureService}],
  template: `
    <app-function-workflow-binding-selector
      [binding]="binding"
      (bindingChange)="binding = $event">
    </app-function-workflow-binding-selector>

    <section class="selection-state" data-testid="selected-binding">
      <h3>Selected Binding</h3>
      <pre>{{ binding ? (binding | json) : 'No binding selected.' }}</pre>
    </section>
  `
})
class FunctionWorkflowBindingSelectionDemoComponent {
  binding: IWorkflowFunctionBinding | null = null;
}

@Component({
  selector: 'app-function-workflow-binding-selector-visual-fixture',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    FunctionWorkflowBindingLoadingDemoComponent,
    FunctionWorkflowBindingBlockedDemoComponent,
    FunctionWorkflowBindingSelectionDemoComponent
  ],
  template: `
    <main class="workflow-binding-fixture">
      <section class="fixture-section">
        <div class="section-header">
          <h2>Loading inventory</h2>
          <p>Progress state before Connect tool inventory returns.</p>
        </div>
        <div class="fixture-actions">
          <button type="button" mat-stroked-button (click)="restartLoadingDemo()">Restart loading demo</button>
        </div>
        @if (showLoadingDemo) {
          <app-function-workflow-binding-loading-demo></app-function-workflow-binding-loading-demo>
        }
      </section>

      <section class="fixture-section">
        <div class="section-header">
          <h2>Ready and blocked inventory</h2>
          <p>Blocked rows stay disabled while ready rows remain selectable.</p>
        </div>
        <app-function-workflow-binding-blocked-demo></app-function-workflow-binding-blocked-demo>
      </section>

      <section class="fixture-section">
        <div class="section-header">
          <h2>Valid selection flow</h2>
          <p>Click the ready workflow to confirm the saved binding payload updates.</p>
        </div>
        <app-function-workflow-binding-selection-demo></app-function-workflow-binding-selection-demo>
      </section>
    </main>
  `,
  styles: [`
    .workflow-binding-fixture {
      display: grid;
      gap: 24px;
      margin: 0 auto;
      max-width: 960px;
      padding: 24px;
    }

    .fixture-section {
      border: 1px solid #d7dee8;
      border-radius: 8px;
      padding: 20px;
      background: #ffffff;
    }

    .section-header {
      margin-bottom: 16px;
    }

    .section-header h2 {
      margin: 0 0 8px;
      font-size: 20px;
    }

    .section-header p {
      margin: 0;
      color: #516070;
    }

    .fixture-actions {
      margin-bottom: 16px;
    }

    .selection-state {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #e4eaf1;
    }

    .selection-state h3 {
      margin: 0 0 8px;
      font-size: 16px;
    }

    .selection-state pre {
      margin: 0;
      white-space: pre-wrap;
      word-break: break-word;
      background: #f6f8fb;
      border-radius: 6px;
      padding: 12px;
    }
  `]
})
export class FunctionWorkflowBindingSelectorVisualFixtureComponent {
  showLoadingDemo = true;

  restartLoadingDemo(): void {
    this.showLoadingDemo = false;
    setTimeout(() => {
      this.showLoadingDemo = true;
    }, 0);
  }
}
